/**
 * React bindings.
 *
 * `react` is an *optional* peer — the engine works headless. These hooks are
 * lazily importing-safe: they `require`-free import from `react`, so a non-
 * React consumer that never imports `hooks` pays nothing.
 *
 *  - {@link useSyncStatus}   — live {@link ProgressSnapshot} + online flag.
 *  - {@link useOutboxEntries} — live pending {@link OutboxEntry} list (for an
 *    optimistic overlay).
 *  - {@link useConflicts}    — live unresolved-conflict list + a resolver.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { SyncQueue } from './sync-engine'
import type {
	OutboxEntry,
	ProgressSnapshot,
	SyncConfirmation,
	SyncConflict,
} from './types'
import {
	validateUseConfirmationsInput,
	validateUseConflictsInput,
	validateUseOutboxEntriesInput,
	validateUseSyncStatusInput,
} from './validate-input'

export interface SyncStatus extends ProgressSnapshot {
	online: boolean
}

/**
 * Subscribe to the queue's progress + connectivity. Re-renders on every state
 * transition the engine reports via `onProgress`. Note: pass a `SyncQueue`
 * constructed with an `onProgress` that forwards to this hook is *not*
 * required — the hook polls `getProgress()` after mount and on a light
 * interval, plus listens to the engine. To keep the engine the single source
 * of truth we re-read counts on the same cadence as the engine's poll loop.
 */
export function useSyncStatus(
	queue: SyncQueue,
	refreshMs = 1000,
): SyncStatus {
	// Wave 10 zod-mandate — runtime validation at the public boundary.
	validateUseSyncStatusInput(queue, refreshMs)
	const [status, setStatus] = useState<SyncStatus>({
		pending: 0,
		in_flight: 0,
		succeeded: 0,
		failed: 0,
		online: queue.isOnline,
	})

	useEffect(() => {
		let alive = true
		const refresh = async () => {
			const snap = await queue.getProgress()
			if (alive) setStatus({ ...snap, online: queue.isOnline })
		}
		void refresh()
		const t = setInterval(() => void refresh(), refreshMs)
		return () => {
			alive = false
			clearInterval(t)
		}
	}, [queue, refreshMs])

	return status
}

/**
 * Subscribe to the queue's `pending` outbox entries. Re-renders whenever the
 * pending set changes — an `enqueue` adds one, a confirm/drain removes one —
 * refreshed on the same light interval as {@link useSyncStatus} (default 1s).
 * Entries come back in FIFO (ascending-id) order, the order the engine drains
 * them, so a host can fold these unsynced mutations over server state to render
 * an optimistic overlay. `limit` caps how many are read (default: all pending).
 *
 * Mirrors {@link useSyncStatus}'s poll-based subscription: it re-reads the
 * engine's own live source ({@link SyncQueue.listPending}) rather than holding a
 * separate store reference, keeping the engine the single source of truth.
 */
export function useOutboxEntries(
	queue: SyncQueue,
	refreshMs = 1000,
	limit?: number,
): OutboxEntry[] {
	// Wave 10 zod-mandate — runtime validation at the public boundary.
	validateUseOutboxEntriesInput(queue, refreshMs, limit)
	const [entries, setEntries] = useState<OutboxEntry[]>([])

	useEffect(() => {
		let alive = true
		const refresh = async () => {
			const pending = await queue.listPending(limit)
			if (alive) setEntries(pending)
		}
		void refresh()
		const t = setInterval(() => void refresh(), refreshMs)
		return () => {
			alive = false
			clearInterval(t)
		}
	}, [queue, refreshMs, limit])

	return entries
}

/**
 * Subscribe to per-item server confirmations for the lifetime of the component.
 *
 * `onConfirmation` is called for every confirmed item — `applied` or
 * `duplicate` — with the authoritative `server_row` and, when the enqueue
 * supplied one, the originating `tempId`. This is the reactive form of
 * {@link SyncQueue.subscribeConfirmations}, and it is the whole reason a host
 * never needs to wrap `fetchImpl` to observe confirmations.
 *
 * The callback is held in a ref, so an inline arrow does NOT resubscribe on
 * every render — the subscription's identity depends only on `queue`. That
 * matters here in a way it does not for the polling hooks: confirmations are an
 * ephemeral stream, so a resubscribe window could silently drop an event.
 *
 * @example
 * ```tsx
 * useConfirmations(queue, (c) => {
 *   if (c.tempId) reconcile(c.tempId, c.serverRow)   // temp id → real row
 * })
 * ```
 */
export function useConfirmations(
	queue: SyncQueue,
	onConfirmation: (confirmation: SyncConfirmation) => void,
): void {
	// Wave 10 zod-mandate — runtime validation at the public boundary.
	validateUseConfirmationsInput(queue, onConfirmation)
	const ref = useRef(onConfirmation)
	// Assign during render rather than in an effect: the subscription below may
	// deliver an event before effects flush, and it must reach the latest handler.
	ref.current = onConfirmation

	useEffect(
		() => queue.subscribeConfirmations((c) => ref.current(c)),
		[queue],
	)
}

export interface UseConflictsResult {
	conflicts: SyncConflict[]
	resolve: (
		clientEventUuid: string,
		decision: 'keep' | 'discard',
	) => Promise<void>
}

/**
 * Subscribe to the live unresolved-conflict list (populated only under the
 * `manual` conflict policy or hard rejections) and expose a resolver.
 */
export function useConflicts(queue: SyncQueue): UseConflictsResult {
	// Wave 10 zod-mandate — runtime validation at the public boundary.
	validateUseConflictsInput(queue)
	const [conflicts, setConflicts] = useState<SyncConflict[]>(() =>
		queue.getConflicts(),
	)

	useEffect(() => {
		const unsub = queue.subscribeConflicts(setConflicts)
		return unsub
	}, [queue])

	const resolve = useCallback(
		(clientEventUuid: string, decision: 'keep' | 'discard') =>
			queue.resolveConflict(clientEventUuid, decision),
		[queue],
	)

	return { conflicts, resolve }
}
